import {Box, Button, Dialog, FormControlLabel, MenuItem, Switch, TextField, Toolbar, Typography} from "@mui/material";
import React, {ChangeEventHandler, KeyboardEventHandler, ReactElement, useContext, useEffect, useState} from "react";
import {FirebaseContext} from "../../firebase/FirebaseProvider";
import {connect} from "react-redux";
import {selectConstants} from "../../../redux/selectors/firebase";
import {Constants} from "../StateTags";
import {IShortcut} from "./Shortcuts";

interface IShortcutActionAddTagToSelected {
    constants: Constants
    data: {
        type: string
        value: string | boolean | number
    }
    onDataUpdate: (data: any) => void
}
function ShortcutActionAddTagToSelected({constants, data, onDataUpdate}: IShortcutActionAddTagToSelected) {
    const handleTagChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        onDataUpdate({type: target.value, value: ""});
    const handleTagValueChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        onDataUpdate({type: data.type, value: target.value});
    const select = constants[data.type]?.type === "string" && Object.values(constants[data.type].constraints || {})
        .find(constraint => constraint.type === "select");
    let value: ReactElement | null;

    if (select) {
        value = (
            <Box display="flex">
                <TextField label="Value" select value={data.value} size="small" onChange={handleTagValueChange}
                           sx={{flexGrow: 1, margin: 1}}>
                    {Object.entries(select.data.values).map(([key, name]) =>
                        <MenuItem key={key} value={key}>{name as string}</MenuItem>
                    )}
                </TextField>
            </Box>
        )
    } else if (constants[data.type]?.type === "boolean") {
        value = <FormControlLabel label="Value" control={
            <Switch size="small" sx={{margin: 2}} checked={data.value === true}
                    onChange={({target}) =>
                        onDataUpdate({type: data.type, value: target.checked})}/>
        }/>
    } else if (constants[data.type]?.type === "number") {
        value = <TextField label="Value" onChange={handleTagValueChange} size="small" sx={{margin: 1}} type="number" value={data.value}/>
    } else {
        value = <TextField label="Value" onChange={handleTagValueChange} size="small" sx={{margin: 1}} value={data.value}/>
    }

    return (
        <Box display="flex" flexDirection="column">
            <TextField label="Tag" onChange={handleTagChange} select size="small"
                       sx={{margin: 1}} value={data.type}>
                {Object.entries(constants).filter(([, constant]) => constant.name)
                    .map(([key, constant]) =>
                        <MenuItem key={key} value={key}>{constant.name}</MenuItem>
                    )}
            </TextField>
            <Box>
                {value}
            </Box>
        </Box>
    );
}

const defaultAction = {type: "addTagToSelected", data: {}};
const defaultEvent = {key: "", ctrlKey: false, shiftKey: false, altKey: false};
interface IShortcutDialog {
    constants: Constants
    edit: string
    onClose: () => void
    project: string
    shortcuts: {[key: string]: IShortcut}
}
function ShortcutDialog({constants, edit, onClose, project, shortcuts}: IShortcutDialog) {
    const {set} = useContext(FirebaseContext);
    const [action, setAction] = useState<{type: string, data: any}>(defaultAction);
    const [event, setEvent] = useState(defaultEvent);
    const [name, setName] = useState("");
    const [identifier, setIdentifier] = useState("");
    const handleActionChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        setAction({type: target.value, data: {}});
    const handleActionDataUpdate = (data: any) => setAction(action => ({type: action.type, data}));
    const handleNameChange: ChangeEventHandler<HTMLInputElement> = ({currentTarget}) =>
        setName(currentTarget.value);
    const handleShortcutUpdate = () =>
        set(`shortcuts/${project}/${edit}`, {
            action,
            event,
            name,
            identifier
        }).then(onClose);
    const handleShortcutKeyDown:KeyboardEventHandler<HTMLInputElement> = (event) => {
        if (event.key.match(/^[a-z0-9]$/i)) {
            let identifier = "";

            if (event.ctrlKey) {
                identifier += "Control + "
            }
            if (event.altKey) {
                identifier += "Alt + "
            }
            if (event.shiftKey) {
                identifier += "Shift + "
            }
            identifier += event.key.toUpperCase();
            setEvent({key: event.key, ctrlKey: event.ctrlKey, shiftKey: event.shiftKey, altKey: event.altKey})
            setIdentifier(identifier);
        }
        event.preventDefault();
        event.stopPropagation();
    }

    useEffect(() => {
        if (edit) {
            setAction(shortcuts[edit].action || defaultAction);
            setEvent(shortcuts[edit].event || defaultEvent);
            setName(shortcuts[edit].name || "");
            setIdentifier(shortcuts[edit].identifier || "");
        }
    }, [edit, setName, setIdentifier, shortcuts]);
    return (
        <Dialog open={Boolean(edit)} onClose={onClose}>
            <Toolbar>
                <Typography variant="h6">Edit Shortcut</Typography>
            </Toolbar>
            <Box display="flex" flexDirection="column" m={2}>
                <TextField label="Name" value={name} onChange={handleNameChange} size="small" sx={{margin: 1}}/>
                <TextField label="Shortcut" value={identifier} onKeyDown={handleShortcutKeyDown}
                           size="small" sx={{margin: 1}}/>
                <Box display="flex" flexDirection="column">
                    <TextField label="Action" onChange={handleActionChange} select size="small"
                               value={action?.type ?? "addTagToSelected"} sx={{margin: 1}}>
                        <MenuItem value="addTagToSelected">Add Tag to Selected</MenuItem>
                    </TextField>
                    {action.type === "addTagToSelected" &&
                        <ShortcutActionAddTagToSelected constants={constants} data={action.data} onDataUpdate={handleActionDataUpdate}/>
                    }
                </Box>
                <Box display="flex" justifyContent="right" m={1}>
                    <Button onClick={handleShortcutUpdate} variant="contained">Update</Button>
                </Box>
            </Box>
        </Dialog>
    );
}
const mapStateToProps = (store: never) => ({
    constants: selectConstants(store)
});

const connected = connect(mapStateToProps, {
})(ShortcutDialog);
export {connected as ShortcutDialog}